import { TwilioConfigInput } from '@pypestream/api-services/urql';

// Imported from the module, not the `utils` barrel: the barrel drags in the
// XState graph and with it `api-services`, which this package's vitest setup
// stubs — a barrel import makes this builder untestable in isolation.
import { encodeToBase64 } from '../../utils/encode-base64';
import { resolveConversationHandling } from './flux-rules';
import type { VoiceChannelFormValues } from './voice-channel-settings-form';

/** Everything the payload needs; `botName` and `env` are channel-level, not config. */
export type VoiceAiConfigFormData = Omit<
  VoiceChannelFormValues,
  'botName' | 'env'
>;

/**
 * Builds a Voice AI channel's complete `twilio_params` payload.
 *
 * Extracted from the form (the way `resolveConversationHandling` already was)
 * so the one rule that is easy to break silently can be tested: this payload
 * REBUILDS THE WHOLE MAP, and the Hasura request transform nulls every key the
 * client omits. A field dropped here is not a missing field — it is that
 * setting being cleared on the next save of anything else (PSTUDIO-4090).
 *
 * The return type makes `enableCallRecording` required, which the generated
 * `TwilioConfigInput` does not, so the omission at least reads as intentional.
 * Note that it is NOT a build gate: this package's `tsc` is a solution file
 * with `files: []`, so no typecheck runs. The tests are the gate.
 */
export const buildVoiceAiConfig = (
  formData: VoiceAiConfigFormData
): TwilioConfigInput & { enableCallRecording: boolean } => {
  const encodedMetadata = formData.elevenlabsVoiceMetadata
    ? encodeToBase64(JSON.stringify(formData.elevenlabsVoiceMetadata))
    : null;

  const hasLaConfig =
    formData.laPlatform ||
    formData.laClientId ||
    formData.laClientSecret ||
    formData.laGrantType ||
    formData.laInstanceUrl ||
    formData.laOAuthUrl;

  const encodedLaConfig = hasLaConfig
    ? encodeToBase64(
        JSON.stringify({
          integrator: formData.laPlatform,
          client_id: formData.laClientId,
          client_secret: formData.laClientSecret,
          grant_type: formData.laGrantType,
          instance_url: formData.laInstanceUrl,
          o_auth_url: formData.laOAuthUrl,
        })
      )
    : null;

  const handoffRules = formData.handoffRules?.filter(
    (r) => r.skillTag && r.destination
  );
  const encodedHandoffOptions =
    handoffRules?.length > 0
      ? encodeToBase64(
          JSON.stringify(
            Object.fromEntries(
              handoffRules.map(
                ({ skillTag, destination, type: handoffType }) => [
                  skillTag,
                  { destination, type: handoffType },
                ]
              )
            )
          )
        )
      : null;

  const conversationHandling = resolveConversationHandling({
    speechModel: formData.speechModel,
    inboundMessageBuffer: formData.inboundMessageBuffer,
    userSilenceTimeout: formData.userSilenceTimeout,
    eotThreshold: formData.eotThreshold,
  });

  return {
    accountSid: formData.accountSid,
    authToken: formData.authToken,
    language: formData.language,
    phoneNumber: formData.phoneNumber,
    speechModel: formData.speechModel,
    transcriptionProvider: formData.transcriptionProvider,
    ttsProvider: formData.ttsProvider,
    elevenlabsAudioModel: formData.elevenlabsAudioModel,
    elevenlabsVoiceId: formData.elevenlabsVoiceId,
    elevenlabsSimilarity: String(formData.elevenlabsSimilarity),
    elevenlabsSpeed: String(formData.elevenlabsSpeed),
    elevenlabsStability: String(formData.elevenlabsStability),
    elevenlabsVoiceMetadataB64: encodedMetadata,
    metadataIntegrationInfoB64: encodedLaConfig,
    voiceHandoffOptionsB64: encodedHandoffOptions,
    userSilenceTimeout: conversationHandling.userSilenceTimeout,
    inboundMessageBufferMs: conversationHandling.inboundMessageBufferMs,
    // TODO(PSTUDIO-3630): send conversationHandling.eotThreshold once the
    // backend exposes TwilioConfigInput.eotThreshold. GraphQL rejects an entire
    // mutation containing an unknown input field, so sending it early would
    // break every voice channel save, not just this setting.
    promptMaxRetries: formData.promptMaxRetries,
    retryPromptPrefix: formData.retryPromptPrefix,
    // Always sent, touched or not — see VoiceChannelFormValues. Coerced
    // because `initialValues` is Partial: a caller omitting the key would
    // otherwise drop it from the JSON and reintroduce the same bug.
    enableCallRecording: formData.enableCallRecording === true,
  };
};
